import React, { useState } from 'react';
import { Box, Typography, IconButton, Skeleton } from '@mui/material';

const ACCENT = '#2C3E2D';
const RULE = '#E4DFD9';
const MUTED = '#74706B';
const FAINT = '#B8B3AD';
const INK = '#0C0C0B';
const WHITE = '#FFFFFF';
const STRIPE = '#F8F6F2';
const BORDER = '1px solid #E4DFD9';
const FONT_HEADING = '"Playfair Display", Georgia, serif';
const FONT_BODY = '"DM Sans", system-ui, sans-serif';

export interface DataListColumn<T> {
  id: string;
  render: (row: T) => React.ReactNode;
  gridMin?: string;
  gridMax?: string;
}

export interface DataListGroup {
  key: string;
  label: string;
  dotColor?: string;
}

interface ResolvedGroup<T> {
  key: string;
  label: string;
  dotColor: string;
  items: T[];
}

export interface DataListProps<T> {
  columns: DataListColumn<T>[];
  data: T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  getAccentColor?: (row: T) => string;
  defaultAccentColor?: string;
  renderActions?: (row: T) => React.ReactNode;
  groups?: DataListGroup[];
  groupBy?: (row: T) => string;
  groupLabel?: (group: DataListGroup, count: number) => string;
  initialShow?: number;
  loading?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  accentWidth?: number;
  gridGap?: string;
  skeletonCount?: number;
  skeletonHeight?: number;
  stripeRows?: boolean;
}

function deriveGridCols<T>(columns: DataListColumn<T>[], accentWidth: number): string {
  const cols: string[] = [`${accentWidth}px`];
  for (const col of columns) {
    const min = col.gridMin ?? '80px';
    const max = col.gridMax ?? '1fr';
    cols.push(`minmax(${min}, ${max})`);
  }
  cols.push('44px');
  return cols.join(' ');
}

export default function DataList<T>({
  columns,
  data,
  rowKey,
  onRowClick,
  getAccentColor,
  defaultAccentColor = ACCENT,
  renderActions,
  groups,
  groupBy,
  groupLabel,
  initialShow = 10,
  loading = false,
  emptyTitle = 'No data found',
  emptyDescription = 'No matching records',
  accentWidth = 3,
  gridGap = '2px',
  skeletonCount = 4,
  skeletonHeight = 56,
  stripeRows = false,
}: DataListProps<T>) {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const gridCols = deriveGridCols(columns, accentWidth);

  const groupedData = React.useMemo((): ResolvedGroup<T>[] => {
    if (!groups && !groupBy) {
      return [{ key: '__all', label: '', dotColor: ACCENT, items: data }];
    }
    if (groups && groupBy) {
      return groups
        .map(g => ({
          ...g,
          dotColor: g.dotColor || ACCENT,
          items: data.filter(r => groupBy(r) === g.key),
        }))
        .filter(g => g.items.length > 0);
    }
    if (groups && !groupBy) {
      return [];
    }
    if (groupBy && !groups) {
      const map = new Map<string, { dotColor: string; items: T[] }>();
      for (const row of data) {
        const key = groupBy(row);
        if (!map.has(key)) map.set(key, { dotColor: ACCENT, items: [] });
        map.get(key)!.items.push(row);
      }
      return Array.from(map.entries()).map(([key, val]) => ({
        key,
        label: key,
        dotColor: val.dotColor,
        items: val.items,
      }));
    }
    return [];
  }, [data, groups, groupBy]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: gridGap }}>
        {Array.from({ length: skeletonCount }).map((_, i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            height={skeletonHeight}
            sx={{ bgcolor: '#E8E6E1' }}
          />
        ))}
      </Box>
    );
  }

  if (!data.length) {
    return (
      <Box sx={{ py: 8, textAlign: 'center', bgcolor: WHITE, border: BORDER }}>
        <Typography
          sx={{
            fontFamily: FONT_HEADING,
            fontSize: '1.1rem',
            fontWeight: 600,
            color: MUTED,
            mb: 1,
          }}
        >
          {emptyTitle}
        </Typography>
        <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.78rem', color: FAINT }}>
          {emptyDescription}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ overflowX: 'auto', pb: '2px' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: gridGap }}>
        {groupedData.map(group => {
          const expanded = expandedGroups.has(group.key);
          const visibleItems = expanded ? group.items : group.items.slice(0, initialShow);
          const hasMore = group.items.length > initialShow;

          return (
            <React.Fragment key={group.key}>
              {group.label ? (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: '12px', pt: '16px', pb: '7px' }}
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: group.dotColor || ACCENT,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        fontFamily: FONT_BODY,
                        fontSize: '0.54rem',
                        fontWeight: 600,
                        letterSpacing: '0.2em',
                        textTransform: 'uppercase',
                        color: MUTED,
                      }}
                    >
                      {group.label}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, height: 1, bgcolor: RULE }} />
                  <Typography
                    sx={{
                      fontFamily: FONT_BODY,
                      fontSize: '0.54rem',
                      fontWeight: 600,
                      letterSpacing: '0.1em',
                      color: FAINT,
                      bgcolor: WHITE,
                      border: BORDER,
                      px: '8px',
                      py: '2px',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {groupLabel
                      ? groupLabel(group, group.items.length)
                      : `${group.items.length} item${group.items.length !== 1 ? 's' : ''}`}
                  </Typography>
                </Box>
              ) : null}

              {visibleItems.map((row, rowIdx) => {
                const accentColor = getAccentColor?.(row) ?? defaultAccentColor;
                const id = rowKey(row);

                return (
                  <Box
                    key={id}
                    onClick={() => onRowClick?.(row)}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: gridCols,
                      alignItems: 'center',
                      bgcolor: stripeRows && rowIdx % 2 !== 0 ? STRIPE : WHITE,
                      border: BORDER,
                      cursor: onRowClick ? 'pointer' : 'default',
                      position: 'relative',
                      transition: 'border-color 0.12s',
                      '&:hover': { borderColor: 'rgba(44,62,45,0.14)' },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: accentWidth,
                        bgcolor: accentColor,
                        opacity: 0,
                        transition: 'opacity 0.12s',
                      },
                      '&:hover::before': { opacity: 1 },
                      ...(renderActions
                        ? {
                            '&:hover .data-list-actions': {
                              opacity: 1,
                              pointerEvents: 'all',
                            },
                          }
                        : {}),
                    }}
                  >
                    <Box />
                    {columns.map(col => (
                      <Box key={col.id} sx={{ p: '12px', minWidth: 0 }}>
                        {col.render(row)}
                      </Box>
                    ))}
                    {renderActions && (
                      <Box
                        onClick={e => e.stopPropagation()}
                        sx={{
                          pr: '8px',
                          display: 'flex',
                          justifyContent: 'flex-end',
                          position: 'relative',
                          zIndex: 1,
                        }}
                      >
                        <Box
                          className="data-list-actions"
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '2px',
                            opacity: 0,
                            pointerEvents: 'none',
                            transition: 'opacity 0.12s',
                            // Opaque backdrop so icons don't collide with row text
                            bgcolor: 'rgba(255, 255, 255, 0.96)',
                            backdropFilter: 'blur(4px)',
                            pl: 1.5,
                            pr: 0.5,
                            py: 0.25,
                            ml: -1,
                            // Soft fade into the row so the bar doesn't hard-cut content
                            boxShadow: '-12px 0 16px 8px rgba(255, 255, 255, 0.96)',
                          }}
                        >
                          {renderActions(row)}
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })}

              {hasMore && (
                <Typography
                  onClick={() => {
                    setExpandedGroups(prev => {
                      const next = new Set(prev);
                      if (expanded) next.delete(group.key);
                      else next.add(group.key);
                      return next;
                    });
                  }}
                  sx={{
                    fontFamily: FONT_BODY,
                    fontSize: '0.65rem',
                    fontWeight: 500,
                    color: ACCENT,
                    cursor: 'pointer',
                    py: '6px',
                    px: 0,
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {expanded
                    ? `- Show less (${group.items.length} total)`
                    : `+ View ${group.items.length - initialShow} more`}
                </Typography>
              )}
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
}
